import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Platform,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

// 直接定义颜色，避免 @cubemeet/core 导入问题
const MODULE_COLOR = '#F97316'; // travel 模块的橙色

// ============ 类型定义 ============

export type LedgerTabType = 'public' | 'private';

export interface ExpenseStatistics {
  /** 总费用 */
  totalExpense: number;
  /** 已审批收据数 */
  approvedCount: number;
  /** 待审核收据数 */
  pendingCount: number;
  /** 人均费用 */
  averagePerPerson: number;
}

export interface ExpenseStatisticsSectionProps {
  /** 当前激活的标签 (公账/私账) */
  activeTab: LedgerTabType;
  /** 标签切换回调 */
  onTabChange: (tab: LedgerTabType) => void;
  /** 公账收据数量 */
  publicCount: number;
  /** 私账收据数量 */
  privateCount: number;
  /** 统计数据 */
  statistics: ExpenseStatistics;
  /** 货币符号 */
  currency?: string;
  /** 是否是创建者 */
  isCreator?: boolean;
  /** 点击设置按钮 */
  onSettingsPress?: () => void;
  /** 格式化金额 */
  formatCurrency?: (amount: number, currency?: string) => string;
}

// ============ 默认格式化函数 ============

const defaultFormatCurrency = (amount: number, currency?: string): string => {
  const symbol = currency || 'HK$';
  if (Math.abs(amount) >= 10000) {
    return `${symbol}${(amount / 1000).toFixed(1)}K`;
  }
  return `${symbol}${amount.toFixed(2)}`;
};

// ============ 组件 ============

const ExpenseStatisticsSection: React.FC<ExpenseStatisticsSectionProps> = ({
  activeTab,
  onTabChange,
  publicCount,
  privateCount,
  statistics,
  currency = 'HK$',
  isCreator = false,
  onSettingsPress,
  formatCurrency = defaultFormatCurrency,
}) => {
  const [tabWidth, setTabWidth] = useState(0);
  const slideAnim = useRef(new Animated.Value(activeTab === 'public' ? 0 : 1)).current;

  useEffect(() => {
    Animated.timing(slideAnim, {
      toValue: activeTab === 'public' ? 0 : 1,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [activeTab, slideAnim]);

  const indicatorTranslateX = tabWidth > 0
    ? slideAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, tabWidth / 2],
      })
    : 0;

  return (
    <View style={styles.container}>
      {/* 标题和设置按钮 */}
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <PlatformIcon name="account-balance" size={20} color={MODULE_COLOR} family="MaterialIcons" />
          <Text style={styles.title}>费用统计</Text>
        </View>
        {isCreator && onSettingsPress && (
          <TouchableOpacity style={styles.settingsButton} onPress={onSettingsPress}>
            <PlatformIcon name="tune" size={16} color={MODULE_COLOR} family="MaterialIcons" />
            <Text style={styles.settingsText}>设置</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* 账本切换标签 */}
      <View
        style={styles.tabsContainer}
        onLayout={({ nativeEvent }) => {
          const width = nativeEvent.layout.width;
          if (width && Math.abs(width - tabWidth) > 1) {
            setTabWidth(width);
          }
        }}
      >
        {tabWidth > 0 && (
          <Animated.View
            style={[
              styles.tabIndicator,
              {
                width: tabWidth / 2,
                transform: [{ translateX: indicatorTranslateX }],
              },
            ]}
          />
        )}
        <TouchableOpacity
          style={styles.tab}
          onPress={() => onTabChange('public')}
          activeOpacity={0.8}
        >
          <Text style={[styles.tabText, activeTab === 'public' && styles.tabTextActive]}>
            群组账本
          </Text>
          <View style={[styles.tabBadge, activeTab === 'public' && styles.tabBadgeActive]}>
            <Text style={[styles.tabBadgeText, activeTab === 'public' && styles.tabBadgeTextActive]}>
              {publicCount}
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.tab}
          onPress={() => onTabChange('private')}
          activeOpacity={0.8}
        >
          <Text style={[styles.tabText, activeTab === 'private' && styles.tabTextActive]}>
            成员私账
          </Text>
          <View style={[styles.tabBadge, activeTab === 'private' && styles.tabBadgeActive]}>
            <Text style={[styles.tabBadgeText, activeTab === 'private' && styles.tabBadgeTextActive]}>
              {privateCount}
            </Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* 统计数据网格 - 2x2 布局 */}
      <View style={styles.statsGrid}>
        {/* 总费用 */}
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#FFF3E9' }]}>
            <PlatformIcon name="account-balance-wallet" size={20} color={MODULE_COLOR} family="MaterialIcons" />
          </View>
          <Text style={styles.statValue}>{formatCurrency(statistics.totalExpense, currency)}</Text>
          <Text style={styles.statLabel}>总费用</Text>
        </View>

        {/* 已审批收据 */}
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#FFF3E9' }]}>
            <PlatformIcon name="receipt" size={20} color={MODULE_COLOR} family="MaterialIcons" />
          </View>
          <Text style={styles.statValue}>{statistics.approvedCount}</Text>
          <Text style={styles.statLabel}>已审批收据</Text>
        </View>

        {/* 待审核 */}
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#FEF3C7' }]}>
            <PlatformIcon name="more-horiz" size={20} color="#F59E0B" family="MaterialIcons" />
          </View>
          <Text style={styles.statValue}>{statistics.pendingCount}</Text>
          <Text style={styles.statLabel}>待审</Text>
        </View>

        {/* 人均 */}
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#FFF3E9' }]}>
            <PlatformIcon name="people" size={20} color={MODULE_COLOR} family="MaterialIcons" />
          </View>
          <Text style={styles.statValue}>{formatCurrency(statistics.averagePerPerson, currency)}</Text>
          <Text style={styles.statLabel}>人均</Text>
        </View>
      </View>

      {/* 分账模式说明 */}
      <View style={styles.modeIndicator}>
        <PlatformIcon name="info-outline" size={14} color="#9CA3AF" family="MaterialIcons" />
        <Text style={styles.modeText}>实际费用 • 均等模式</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  settingsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#FFF7ED',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: MODULE_COLOR,
    gap: 4,
  },
  settingsText: {
    fontSize: 12,
    color: MODULE_COLOR,
    fontWeight: '500',
  },
  // 标签切换
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    padding: 4,
    marginBottom: 16,
    position: 'relative',
  },
  tabIndicator: {
    position: 'absolute',
    top: 4,
    left: 4,
    bottom: 4,
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
    }),
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    gap: 6,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  tabTextActive: {
    color: MODULE_COLOR,
  },
  tabBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  tabBadgeActive: {
    backgroundColor: MODULE_COLOR,
  },
  tabBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#64748B',
  },
  tabBadgeTextActive: {
    color: '#FFFFFF',
  },
  // 统计网格
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -6,
  },
  statItem: {
    width: '50%',
    paddingHorizontal: 6,
    paddingVertical: 12,
    alignItems: 'center',
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  statValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
  },
  // 分账模式
  modeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    gap: 6,
  },
  modeText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
});

export default ExpenseStatisticsSection;
